// Hardcharger™ launch facts: the single source of truth for /hardcharger, /hardcharger/install,
// /hardcharger/integrations, /chargewire and the home page. Every number and claim here comes from a
// measured result or a README, cited next to it; keep it that way when editing (global CLAUDE.md).

export const HARDCHARGER_DOCS_URL = 'https://docs.hardcharger.gizmodata.com';
export const HARDCHARGER_GITHUB_URL = 'https://github.com/gizmodata/hardcharger';
export const HARDCHARGER_RELEASES_URL = 'https://github.com/gizmodata/hardcharger/releases/latest';
// Paths on the docs site (gizmodata/hardcharger-docs; docs.hardcharger.gizmodata.com goes live at launch).
export const HARDCHARGER_MIGRATION_URL = 'https://docs.hardcharger.gizmodata.com/get-started/coming-from-gizmosql/';
export const HARDCHARGER_QUICKSTART_URL = 'https://docs.hardcharger.gizmodata.com/get-started/quickstart/';
export const hardchargerDocs = {
  python: 'https://docs.hardcharger.gizmodata.com/clients/python/',
  go: 'https://docs.hardcharger.gizmodata.com/clients/go/',
  adbcDriverManager: 'https://docs.hardcharger.gizmodata.com/clients/adbc-driver-manager/',
  jdbc: 'https://docs.hardcharger.gizmodata.com/clients/jdbc/',
  dbeaver: 'https://docs.hardcharger.gizmodata.com/clients/jdbc/#dbeaver',
  javascript: 'https://docs.hardcharger.gizmodata.com/clients/javascript/',
  cli: 'https://docs.hardcharger.gizmodata.com/clients/cli/',
  http: 'https://docs.hardcharger.gizmodata.com/clients/http/',
  sso: 'https://docs.hardcharger.gizmodata.com/guides/sso/',
  bulkIngest: 'https://docs.hardcharger.gizmodata.com/guides/bulk-ingest/',
  webUi: 'https://docs.hardcharger.gizmodata.com/guides/web-ui/',
  chargewire: 'https://docs.hardcharger.gizmodata.com/concepts/chargewire/',
};
export const CHARGEWIRE_SITE_URL = 'https://chargewire.gizmodata.com';
export const CHARGEWIRE_GITHUB_URL = 'https://github.com/gizmodata/chargewire';
export const HARDCHARGER_SLACK_URL = 'https://join.slack.com/t/gizmodatacommunity/shared_invite/zt-42jk7kdti-SJp7qk7d6XYCIKfBG~4HrA';

// The bull identity v3 art, converted by scripts/responsive-images.mjs.
export const hardchargerArt = {
  stacked: {
    dark: {
      src: '/hardcharger/brand/mascot-stacked-dark.webp',
      srcSet: '/hardcharger/brand/mascot-stacked-dark-480w.webp 480w, /hardcharger/brand/mascot-stacked-dark-800w.webp 800w, /hardcharger/brand/mascot-stacked-dark-1200w.webp 1200w, /hardcharger/brand/mascot-stacked-dark.webp 1600w',
    },
    light: {
      src: '/hardcharger/brand/mascot-stacked-light.webp',
      srcSet: '/hardcharger/brand/mascot-stacked-light-480w.webp 480w, /hardcharger/brand/mascot-stacked-light-800w.webp 800w, /hardcharger/brand/mascot-stacked-light-1200w.webp 1200w, /hardcharger/brand/mascot-stacked-light.webp 1600w',
    },
    width: 1600,
    height: 1311,
  },
  horizontal: {
    dark: { src: '/hardcharger/brand/mascot-horizontal-dark.webp', srcSet: '/hardcharger/brand/mascot-horizontal-dark-800w.webp 800w, /hardcharger/brand/mascot-horizontal-dark.webp 1600w' },
    light: { src: '/hardcharger/brand/mascot-horizontal-light.webp', srcSet: '/hardcharger/brand/mascot-horizontal-light-800w.webp 800w, /hardcharger/brand/mascot-horizontal-light.webp 1600w' },
    width: 1600,
    height: 488,
  },
  head: '/hardcharger/brand/head-128.webp',
  og: '/hardcharger/brand/hardcharger-og.png',
};

// The 1 TB TPC-H-derived run (TPCH_SF1000), the ClickBench dry run and the launch date/versions live in
// src/data/hardcharger-launch.ts, the one file a GA re-run edits.

// Remote fetch and ingest, 2026-10-19 (hardcharger docs/benchmarks.md, "LZ4, selective compression and
// result-sized splits"): two AWS c8gn.8xlarge in one placement group (LAN) or a client in another region,
// ~46 ms away (WAN); TLS on every server unless marked plaintext; median of 5 runs; seconds, lower is better.
// Hardcharger with the Chargewire ADBC driver's defaults; Quack attached from DuckDB 2.0 (dev build
// 2.0.0.dev2610161340); GizmoSQL 1.42.0 over Arrow Flight SQL. † Quack without insertion order (its default
// keeps it); Hardcharger's parallel upload parts keep no row order either.
export const REMOTE_BENCH = {
  date: '2026-10-19',
  rows: [
    { workload: 'Fetch SF1 lineitem (6M rows), LAN', chargewire: 0.126, quack: 0.164, flight: 1.484 },
    { workload: 'Fetch SF10 lineitem (60M rows), LAN', chargewire: 0.843, quack: 0.815, flight: 16.105 },
    { workload: 'Fetch 100M narrow rows, LAN', chargewire: 0.453, quack: 0.390, flight: 5.333 },
    { workload: 'Load SF1 lineitem, LAN', chargewire: 0.538, quack: 0.600, quackNote: '†', flight: 1.607 },
    { workload: 'Load SF10 lineitem, LAN', chargewire: 2.835, quack: 2.667, quackNote: '†', flight: 9.416 },
    { workload: 'Fetch SF1 lineitem, WAN (46 ms)', chargewire: 0.383, quack: 1.508, flight: 12.972 },
    { workload: 'Load SF1 lineitem, WAN (46 ms)', chargewire: 1.107, quack: 1.801, quackNote: '†', flight: 13.802 },
    { workload: 'Fetch SF10 lineitem, LAN, plaintext', chargewire: 0.746, quack: 0.962, flight: 13.216 },
    { workload: 'Load SF10 lineitem, LAN, plaintext', chargewire: 2.621, quack: 2.596, quackNote: '†', flight: 9.460 },
  ],
};

export const BENCHMARKS_DOC_URL = 'https://github.com/gizmodata/hardcharger/blob/main/docs/benchmarks.md';
export const QUACK_DOCS_URL = 'https://duckdb.org/docs/current/quack/overview';
